import { useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { CellPosition, CellValueChangedEvent, TabToNextCellParams } from 'ag-grid-community';

import dayjs from 'dayjs';

import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import SectionCard from '@/components/common/SectionCard/SectionCard';
import AgGridCellRenderer from '@/components/common/TableGrid/CustomComponents/AgGridCellRenderer/AgGridCellRenderer';
import AgGridDatepickerTag4Editor
  from '@/components/common/TableGrid/CustomComponents/AgGridDatepicker/AgGridDatepickerTag4Editor';
import AgGridDropdownEditor from '@/components/common/TableGrid/CustomComponents/AgGridDropdown/AgGridDropdownEditor';
import AgGridProductDropdownEditorAsync
  from '@/components/common/TableGrid/CustomComponents/AgGridDropdown/AgGridProductDropdownEditorAsync';
import AgGridRemoveButton from '@/components/common/TableGrid/CustomComponents/AgGridRemoveButton/AgGridRemoveButton';
import AgGridSaveButton from '@/components/common/TableGrid/CustomComponents/AgGridSaveButton/AgGridSaveButton';
import { TableGrid } from '@/components/common/TableGrid/TableGrid';
import useGetContractDetails from '@/hooks/contract/useGetContractDetails.hook';
import { getParameterizedLink } from '@/utils/getLinkWithSubscriptionId.utils';
import useCreateContractLines, { ContractLineToCreate, getEmptyContractLineData } from './hooks/useCreateContractLine';
import { validator } from './validation/validator';
import { useColumns } from './columns';
import { notificationService } from '@/services/notification/notification.client';
import RollOverDateModal from
  '../ContractDetails/RightPanel/Statuses/components/ActiveStatus/components/RolloverDateModal/RollOverDateModal';
import { ContractStatuses } from '@/config/statuses.config';
import AgGridCustomerDropdownEditorAsync from
  '@/components/common/TableGrid/CustomComponents/AgGridDropdown/AgGridCustomerDropdownEditorAsync';
import AgGridPurchaseOrderDropdownEditorAsync from
  '@/components/common/TableGrid/CustomComponents/AgGridDropdown/AgGridPurchaseOrderDropdownEditorAsync';
import { contractsBCPath } from '@/utils/path.utlis';
import { useFocusSaveOnCellEdit } from './hooks/useFocusSaveOnCellEdit';
import { ContractOriginEnum } from '@/graphql/contracts/_generated_/contracts';

export default function ContractLinesContainer() {
  const gridRef = useRef<AgGridReact>(null);
  const { contractId }: { contractId: UUID } = useParams();
  const { contract } = useGetContractDetails(contractId);
  const routerHistory = useHistory();
  const [isAllValuesValid, setIsAllValuesValid] = useState(false);
  const [showRollOverModal, setShowRollOverModal] = useState(false);
  const { createContractLines } = useCreateContractLines(contractId, ContractOriginEnum.CnB);
  const columns = useColumns();
  const firstEditableColumnIndex = 2;
  const rowIndex = new URL(window.location.href).searchParams.get('rowIndex') ?? '';
  const rowIndexQuery = `?rowIndex=${rowIndex}`;

  const isExpiredDNRContractWithRenewableTerm = contract?.renewableTerm && contract.renewableTerm > 0 &&
    contract?.status.status === ContractStatuses.DNR && dayjs().isAfter(new Date(contract?.contractEndDate));

  const { onCellFocusChanged } = useFocusSaveOnCellEdit(gridRef);

  const setLastRowEditable = () => {
    if (!gridRef.current) return;

    const firstColumn = gridRef.current.api.getAllDisplayedColumns()[firstEditableColumnIndex];
    const lastRowIndex = gridRef.current.api.getDisplayedRowCount() - 1;

    if (lastRowIndex < 0) return;

    gridRef.current.api.ensureColumnVisible(firstColumn);
    gridRef.current.api.setFocusedCell(lastRowIndex, firstColumn.getColId());

    gridRef.current.api.startEditingCell({
      rowIndex: lastRowIndex,
      colKey: firstColumn.getColId()
    });
  };

  const validateAddedContractLines = () => {
    setIsAllValuesValid(() => {
      if (!gridRef.current) return false;
      let isValid = true;

      gridRef.current.api.forEachNode(({ data: contractLine }) => {
        if (!validator.isValidContractLine(contractLine, { contract })) {
          isValid = false;
        }
      });

      return isValid;
    });
  };

  const addNewRow = () => {
    if (!gridRef.current) return;

    const emptyContractLine = getEmptyContractLineData(contract);
    const newRowIndex = gridRef.current.api.getDisplayedRowCount();

    gridRef.current.api.applyTransaction({
      add: [emptyContractLine],
      addIndex: newRowIndex
    });

    setLastRowEditable();
    validateAddedContractLines();
  };

  const onRowValueChanged = () => {
    validateAddedContractLines();
  };

  const onRowDataUpdated = () => {
    validateAddedContractLines();
  };

  const saveContractLine = () => {
    if (!gridRef.current) return;
    const contractLines = gridRef.current.api.getRenderedNodes().map((node) => node.data);
    createContractLines(contractLines as ContractLineToCreate[])
      .then((createdContactLinesResults) => {
        let isAllSuccessfully = true;

        if (!createdContactLinesResults.success) {
          isAllSuccessfully = false;
        }

        if (isAllSuccessfully) {
          notificationService.open({
            title: 'Create Contract Lines',
            text: 'Contract Lines successfully created',
            type: 'success'
          });

          setTimeout(() => {
            routerHistory.push(
              getParameterizedLink(`/${contractsBCPath}/${contractId}${rowIndexQuery}`)
            );
          });
        }
      });
  };

  const saveContractLineWithConfirm = () => {
    if (!gridRef.current) return;
    if (isExpiredDNRContractWithRenewableTerm) {
      setShowRollOverModal(true);
      return;
    }
    saveContractLine();
  };

  const onGridReady = () => {
    if (!gridRef.current) return;

    gridRef.current.api.applyTransaction({
      add: [getEmptyContractLineData(contract)],
      addIndex: 0
    });

    setLastRowEditable();
  };

  const onSaveCellClicked = (e: CellValueChangedEvent<Partial<ContractLineToCreate>>) => {
    validateAddedContractLines();

    if (e.colDef.field === 'billingType') {
      e.api.refreshCells({
        rowNodes: [e.node],
        columns: ['unitPriceFrequency', 'billingInterval'],
        force: true,
      });
    }
  };

  const cancelCreating = () => {
    const link = getParameterizedLink(`/${contractsBCPath}/${contractId}${rowIndexQuery}`);
    routerHistory.push(link);
  };

  const tabToNextCell = (params: TabToNextCellParams): CellPosition => {
    const { api, backwards, editing, nextCellPosition, previousCellPosition } = params;
    const isLastColumnInRow = nextCellPosition === null;
    const isLastRowInGrid = api.getFocusedCell()?.rowIndex === api.getDisplayedRowCount() - 1;
    const defaultPosistion = nextCellPosition ? nextCellPosition : previousCellPosition;

    if (!editing || backwards || !isLastColumnInRow || !isLastRowInGrid) {
      return defaultPosistion;
    }

    api.stopEditing();
    addNewRow();
    setLastRowEditable();

    const firstColumn = api.getAllDisplayedColumns()[firstEditableColumnIndex];
    const lastRowIndex = api.getDisplayedRowCount() - 1;

    return {
      rowPinned: null,
      rowIndex: lastRowIndex,
      column: firstColumn
    };
  };

  return (
    <>
      <SectionCard
        title={{ titleText: 'Contract Lines' }}
        collapsable={{ buttonText: 'Contract Lines' }}
      >
        {showRollOverModal && (
          <RollOverDateModal
            onClose={() => setShowRollOverModal(false)}
            onConfirm={() => {
              saveContractLine();
              setShowRollOverModal(false);
            }} />)}
        <TableGrid<Partial<ContractLineToCreate>>
          ref={gridRef}
          onRowValueChanged={onRowValueChanged}
          onRowDataUpdated={onRowDataUpdated}
          columnDefs={columns}
          onGridReady={onGridReady}
          onCellValueChanged={onSaveCellClicked}
          onCellFocused={onCellFocusChanged}
          suppressCellFocus={true}
          stopEditingWhenCellsLoseFocus={false}
          components={{
            CustomerDropdownEditorAsync: AgGridCustomerDropdownEditorAsync,
            ProductDropdownEditorAsync: AgGridProductDropdownEditorAsync,
            PurchaseOrderDropdownEditorAsync: AgGridPurchaseOrderDropdownEditorAsync,
            DropdownEditor: AgGridDropdownEditor,
            DatepickerEditor: AgGridDatepickerTag4Editor,
            CellRenderer: AgGridCellRenderer,
            RemoveButton: AgGridRemoveButton,
            SaveButton: AgGridSaveButton
          }}
          tabToNextCell={tabToNextCell}
          defaultColDef={{ resizable: true, sortable: false }}
        />

        <div
          className="d-flex justify-content-start pt-20 pb-20 pl-45 pr-45"
          data-testid='add-record-btn'
        >
          <Button
            buttonStyleType={ButtonStyleType.PRIMARY}
            onClick={addNewRow}
          >
            Add Record
          </Button>
        </div>
      </SectionCard>

      <div className="d-flex justify-content-start pt-20 pb-20">
        <Button
          buttonStyleType={ButtonStyleType.PRIMARY}
          onClick={saveContractLineWithConfirm}
          disabled={!isAllValuesValid}
          className="mr-20"
          data-testid="save-btn"
        >
          Save
        </Button>

        <Button
          buttonStyleType={ButtonStyleType.SECONDARY}
          onClick={cancelCreating}
        >
          Cancel
        </Button>
      </div>
    </>
  );
}
